import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    FileText,
    Calendar,
    DollarSign,
    CheckCircle,
    AlertCircle,
    Loader2,
    Check,
    Building2,
    User,
} from 'lucide-react';
import { contractsService, Contract } from '../../services/contracts.service';

export default function GuestSignContractPage() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [contract, setContract] = useState<Contract | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSigning, setIsSigning] = useState(false);
    const [isSigned, setIsSigned] = useState(false);

    // Signature form state
    const [name, setName] = useState('');
    const [title, setTitle] = useState('');
    const [signatureType, setSignatureType] = useState<'type' | 'draw'>('type');
    const [typedSignature, setTypedSignature] = useState('');
    const [consent, setConsent] = useState(false);

    // Canvas ref for drawing signature
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        const validateToken = async () => {
            if (!id || !token) {
                setError('Invalid signing link. Please request a new link from the contract sender.');
                setIsLoading(false);
                return;
            }

            try {
                const contractData = await contractsService.validateGuestToken(id, token);
                setContract(contractData);

                // Check if already signed by brand
                if (contractData.signatures?.brandSignedAt) {
                    setIsSigned(true);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Invalid or expired link');
            } finally {
                setIsLoading(false);
            }
        };

        validateToken();
    }, [id, token]);

    // Canvas drawing handlers
    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        setIsDrawing(true);
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const getSignatureData = (): string => {
        if (signatureType === 'type') {
            return typedSignature;
        } else {
            const canvas = canvasRef.current;
            if (!canvas) return '';
            return canvas.toDataURL('image/png');
        }
    };

    const handleSign = async () => {
        if (!id || !token || !contract) return;

        const signatureData = getSignatureData();
        if (!signatureData || !name || !consent) {
            return;
        }

        setIsSigning(true);
        try {
            const signData: { token: string; signature: string; name: string; title?: string } = {
                token,
                signature: signatureData,
                name,
            };
            if (title) {
                signData.title = title;
            }
            await contractsService.signByGuest(id, signData);
            setIsSigned(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to sign contract');
        } finally {
            setIsSigning(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-[133.33vh] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
                    <p className="text-slate-600">Loading contract...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error && !contract) {
        return (
            <div className="min-h-[133.33vh] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Link Expired or Invalid</h1>
                    <p className="text-slate-600 mb-6">{error}</p>
                    <p className="text-sm text-slate-500">
                        Please contact the sender to request a new signing link.
                    </p>
                </div>
            </div>
        );
    }

    // Success state (already signed or just signed)
    if (isSigned) {
        return (
            <div className="min-h-[133.33vh] bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-12 h-12 text-green-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-2">Contract Signed!</h1>
                    <p className="text-slate-600 mb-6">
                        Thank you for signing the contract. You can now close this window.
                    </p>
                    <p className="text-sm text-slate-500">
                        A copy of the signed contract will be sent to your email.
                    </p>
                </div>
            </div>
        );
    }

    if (!contract) return null;

    return (
        <div className="min-h-[133.33vh] bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
            <div className="max-w-3xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-primary-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-1">Contract Signing</h1>
                    <p className="text-slate-600">Please review and sign the contract below</p>
                </div>

                {/* Contract Details */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
                    {/* Contract Header */}
                    <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-6">
                        <h2 className="text-xl font-semibold mb-2">{contract.title}</h2>
                        <div className="flex items-center gap-4 text-primary-100 text-sm">
                            <span className="flex items-center gap-1">
                                <Building2 className="w-4 h-4" />
                                {contract.partnership?.brand || 'Brand'}
                            </span>
                            <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                {formatCurrency(contract.totalValue)}
                            </span>
                        </div>
                    </div>

                    {/* Contract Body */}
                    <div className="p-6 space-y-6">
                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-slate-50 rounded-xl p-4">
                                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                                    <Calendar className="w-4 h-4" />
                                    Start Date
                                </div>
                                <p className="font-medium text-slate-900">{formatDate(contract.startDate)}</p>
                            </div>
                            <div className="bg-slate-50 rounded-xl p-4">
                                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                                    <Calendar className="w-4 h-4" />
                                    End Date
                                </div>
                                <p className="font-medium text-slate-900">{formatDate(contract.endDate)}</p>
                            </div>
                        </div>

                        {/* Deliverables */}
                        {contract.deliverables.length > 0 && (
                            <div>
                                <h3 className="font-semibold text-slate-900 mb-3">Deliverables</h3>
                                <ul className="space-y-2">
                                    {contract.deliverables.map((del, idx) => (
                                        <li key={idx} className="flex items-start gap-3 bg-slate-50 rounded-lg p-3">
                                            <Check className="w-5 h-5 text-green-500 mt-0.5" />
                                            <div>
                                                <p className="font-medium text-slate-900">{del.title}</p>
                                                {del.description && (
                                                    <p className="text-sm text-slate-500">{del.description}</p>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Payment Terms */}
                        {contract.paymentTerms && (
                            <div>
                                <h3 className="font-semibold text-slate-900 mb-3">Payment Terms</h3>
                                <div className="bg-slate-50 rounded-xl p-4">
                                    <p className="text-slate-600 capitalize">{contract.paymentTerms.type}</p>
                                    {contract.paymentTerms.milestones?.length > 0 && (
                                        <ul className="mt-3 space-y-2">
                                            {contract.paymentTerms.milestones.map((m, idx) => (
                                                <li key={idx} className="flex justify-between text-sm">
                                                    <span className="text-slate-600">{m.name}</span>
                                                    <span className="font-medium text-slate-900">{m.percentage}%</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Notes */}
                        {contract.notes && (
                            <div>
                                <h3 className="font-semibold text-slate-900 mb-3">Additional Notes</h3>
                                <p className="text-slate-600 bg-slate-50 rounded-xl p-4">{contract.notes}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Signature Section */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">Your Signature</h3>

                    {/* Name & Title */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                <User className="w-4 h-4 inline mr-1" />
                                Full Name *
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="John Doe"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                <Building2 className="w-4 h-4 inline mr-1" />
                                Title (Optional)
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Marketing Director"
                            />
                        </div>
                    </div>

                    {/* Signature Type Toggle */}
                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setSignatureType('type')}
                            className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${signatureType === 'type'
                                ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                                : 'bg-slate-100 text-slate-600 border-2 border-transparent'
                                }`}
                        >
                            Type Signature
                        </button>
                        <button
                            onClick={() => setSignatureType('draw')}
                            className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${signatureType === 'draw'
                                ? 'bg-primary-100 text-primary-700 border-2 border-primary-500'
                                : 'bg-slate-100 text-slate-600 border-2 border-transparent'
                                }`}
                        >
                            Draw Signature
                        </button>
                    </div>

                    {/* Signature Input */}
                    {signatureType === 'type' ? (
                        <div className="mb-6">
                            <input
                                type="text"
                                value={typedSignature}
                                onChange={(e) => setTypedSignature(e.target.value)}
                                className="w-full px-4 py-4 border-2 border-dashed border-slate-300 rounded-xl text-center text-2xl font-signature focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Type your signature"
                                style={{ fontFamily: "'Dancing Script', cursive" }}
                            />
                        </div>
                    ) : (
                        <div className="mb-6">
                            <div className="relative border-2 border-dashed border-slate-300 rounded-xl overflow-hidden">
                                <canvas
                                    ref={canvasRef}
                                    width={500}
                                    height={150}
                                    className="w-full bg-white cursor-crosshair"
                                    onMouseDown={startDrawing}
                                    onMouseMove={draw}
                                    onMouseUp={stopDrawing}
                                    onMouseLeave={stopDrawing}
                                />
                                <button
                                    onClick={clearCanvas}
                                    className="absolute top-2 right-2 text-sm text-slate-500 hover:text-slate-700"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Consent Checkbox */}
                    <label className="flex items-start gap-3 mb-6 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            className="mt-1 w-5 h-5 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-slate-600">
                            I agree to use electronic records and signatures, and I understand that by signing this contract,
                            I am entering into a legally binding agreement.
                        </span>
                    </label>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Sign Button */}
                    <button
                        onClick={handleSign}
                        disabled={isSigning || !name || !consent || (!typedSignature && signatureType === 'type')}
                        className="w-full py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                    >
                        {isSigning ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Signing...
                            </>
                        ) : (
                            <>
                                <Check className="w-5 h-5" />
                                Sign Contract
                            </>
                        )}
                    </button>
                </div>

                {/* Footer */}
                <p className="text-center text-sm text-slate-500 mt-6">
                    This is a secure signing page. Your signature will be legally binding.
                </p>
            </div>
        </div>
    );
}
